import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { auth, setAuthToken } from '@/lib/trackizy';
import { applySession, fetchSession, fetchShellServer } from '@/features/enterprise/sessionBootstrap';
import useBranding from '@/hooks/useBranding';
import { LoginChrome } from './LoginLayouts';

const REMEMBER_KEY = 'trackizy_remembered_email';

export default function LoginPage() {
  const { t } = useTranslation();
  const branding = useBranding();
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { token } = await auth.login(email, password);
      setAuthToken(token, { remember: rememberMe });
      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }

      // Fetch full session (org context, sultan status, server config). A
      // reseller member with no org lands in enterprise mode (fresh token).
      const sessionData = await fetchSession();
        const server = await fetchShellServer(sessionData);
      applySession(sessionData, dispatch, { server });
      // Hub deployment: a sultan's home is the fleet registry, not the org
      // dashboard — the hub has no orgs/devices, so '/' is an empty shell there.
      // A reseller member with no org of their own was put in enterprise mode
      // by fetchSession and lands on the enterprise Companies page.
      if (sessionData.isHub && sessionData.is_sultan) {
        navigate('/sultan/servers', { replace: true });
      } else if (sessionData.enterprise) {
        navigate('/enterprise', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const form = {
    t,
    color: branding.color,
    email, setEmail,
    password, setPassword,
    rememberMe, setRememberMe,
    error, loading,
    onSubmit: handleSubmit,
  };

  return (
    <LoginChrome design={branding.loginDesign} branding={branding} t={t} form={form} />
  );
}
